<script setup lang="ts">
  import { computed } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    coercePhoneAiMinuteCount,
    getPhoneAiProgressBarFillClass,
    getPhoneAiUsageBarPercent
  } from '@/utils/phoneAiUsage'

  const props = withDefaults(
    defineProps<{
      /** Omitted by some API payloads until first usage sync — coerce to 0 */
      usedMinutes?: number
      includedMinutes?: number
      /** `phonebot_self_service_plan` — drives bar fill colours (beginner vs pro-style). */
      plan?: string | null
    }>(),
    {
      usedMinutes: 0,
      includedMinutes: 0,
      plan: null
    }
  )

  const { t } = useTranslation({
    en: {
      minutesUsed: '{used} / {included} minutes used'
    },
    de: {
      minutesUsed: '{used} / {included} Minuten verwendet'
    },
    fr: {
      minutesUsed: '{used} / {included} minutes utilisées'
    }
  })

  const safeUsedMinutes = computed(() => coercePhoneAiMinuteCount(props.usedMinutes))
  const safeIncludedMinutes = computed(() => coercePhoneAiMinuteCount(props.includedMinutes))

  const barPercent = computed(() =>
    getPhoneAiUsageBarPercent(safeUsedMinutes.value, safeIncludedMinutes.value)
  )

  const fillClass = computed(() =>
    getPhoneAiProgressBarFillClass(props.plan, safeUsedMinutes.value, safeIncludedMinutes.value)
  )

  const minutesLabel = computed(() =>
    t('minutesUsed', {
      used: safeUsedMinutes.value,
      included: safeIncludedMinutes.value
    })
  )

  const ariaLabel = computed(() => minutesLabel.value)
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-900/5">
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <p class="text-lg font-semibold tracking-tight text-gray-900">
          {{ minutesLabel }}
        </p>
      </div>

      <div
        class="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        :aria-valuenow="Math.round(barPercent)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="ariaLabel"
      >
        <div
          class="h-full max-w-full rounded-full transition-[width] duration-300 ease-out"
          :class="[fillClass, 'min-w-0']"
          :style="{ width: `${barPercent}%` }"
        />
      </div>
    </div>
  </div>
</template>
